import React from "react"
import styled, { css, keyframes } from "styled-components"
import { HeadingThree, Link } from "buoy-core"
import { responsive } from "buoy-theme"
import { fontWeight } from "buoy-theme/dist/helpers"
import { animateOnScroll } from "buoy-animators"
import withSizes from "react-sizes"
import { STATIC_URL } from "../../common/constants"
import RecommendationCard from "../../common/components/RecommendationCard"

const ONE_COLUMN_RANGES = [[0, 750], [1020, 1240]]

const useOneColumn = width =>
  ONE_COLUMN_RANGES.some(range => {
    return width >= range[0] && width <= range[1]
  })

const moveUp = keyframes`
    0% {

    }
    100% {
        transform: translateY(0);
        opacity: 1;
    }
`

const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin: 0 0 8px 0;
  align-items: baseline;
  ${props =>
    props.animatable
      ? css`
          opacity: 0;
          transform: translateY(100px);
        `
      : ""};

  ${props =>
    props.animate
      ? css`
          animation: ${moveUp} 0.4s ease forwards;
        `
      : ""};
`

const Heading = styled(HeadingThree)`
  font-size: 20px;
  line-height: 28px;
  font-weight: ${fontWeight.light};
  margin: 0;
`

const RecommendationSectionWrapper = styled.div`
  ${responsive("padding-top", "70px", "70px", "84px", "84px", "48px")};
`

const RecommendationTwoColumnCardContainer = styled.div`
  ${responsive("display", "flex", "flex", "flex", "flex", "none")};
  width: 100%;
  flex-flow: column wrap;
`

const RecommendationOneColumnCardContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column wrap;
`

const RecommendationCardRowDesktop = styled.div`
  display: flex;
  > div:first-child {
    margin-right: 12px;
  }
  > div:last-child {
    margin-left: 12px;
  }
`

const animatedHeader = Component =>
  animateOnScroll(
    ({ wrappedRef, expand, label, showViewAll, childAnimate }) => (
      <Component innerRef={wrappedRef} animatable animate={childAnimate[0]}>
        <Heading>{label}</Heading>
        {showViewAll && <Link onClick={() => expand()}>view all</Link>}
      </Component>
    )
  )

const animatedCardContainer = Component =>
  animateOnScroll(({ recommendations, borders, wrappedRef, childAnimate }) => (
    <Component innerRef={wrappedRef}>
      {recommendations.map((rec, index) => (
        <RecommendationCard
          token={rec.token}
          key={index}
          heroImg={rec.hero ? rec.hero.image : null}
          heroText={rec.hero ? rec.hero.text : null}
          image={rec.image ? `${STATIC_URL}${rec.image}` : null}
          borderColor={borders ? "primary" : null}
          tags={rec.tags}
          headline={rec.headline}
          description={rec.description}
          highlights={rec.highlights}
          infoList={rec.info}
          ctaText={rec.ctaText}
          ctaUrl={rec.ctaUrl}
          animate={childAnimate[index]}
          animatable
        />
      ))}
    </Component>
  ))

const AnimatedOneColumnCardContainer = animatedCardContainer(
  RecommendationOneColumnCardContainer
)
const AnimatedTwoColumnCardContainer = animatedCardContainer(
  RecommendationTwoColumnCardContainer
)
const AnimatedHeader = animatedHeader(HeaderWrapper)

class RecommendationSection extends React.Component {
  state = { expand: false }

  expand = () => {
    this.setState({ expand: true })
  }

  collapse = () => {
    this.setState({ expand: false })
  }

  render() {
    const { section, perPage, featured, oneColumn } = this.props
    const recommendations = this.state.expand
      ? section.recommendations
      : section.recommendations.slice(0, perPage)
    const showViewAll = !this.state.expand && recommendations.length > perPage

    return (
      <RecommendationSectionWrapper>
        <AnimatedHeader
          label={section.label}
          expand={this.expand}
          showViewAll={showViewAll}
        />
        {oneColumn ? (
          <AnimatedOneColumnCardContainer
            recommendations={recommendations}
            borders={featured}
          />
        ) : (
          <RecommendationCardRowDesktop>
            <AnimatedTwoColumnCardContainer
              recommendations={recommendations.filter((rec, i) => i % 2 === 0)}
              borders={featured}
            />
            <AnimatedTwoColumnCardContainer
              recommendations={recommendations.filter((rec, i) => i % 2)}
              borders={featured}
            />
          </RecommendationCardRowDesktop>
        )}
      </RecommendationSectionWrapper>
    )
  }
}

const mapSizesToProps = ({ width }) => ({
  oneColumn: useOneColumn(width),
})

RecommendationSection.defaultProps = {
  perPage: 7,
  featured: false,
}

export default withSizes(mapSizesToProps)(RecommendationSection)



// WEBPACK FOOTER //
// ./assets/dashboard/components/RecommendationSection.js